import type { FC } from "react";

export interface WizardStepDescriptor {
  id: string;
  label: string;
}

function barColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "bg-emerald-500";
  }
  if (isActive) {
    return "bg-emerald-500/40";
  }
  return "bg-slate-300 dark:bg-slate-700";
}

function glyphFor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "■";
  }
  if (isActive) {
    return "□";
  }
  return "·";
}

function glyphColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete || isActive) {
    return "text-emerald-600 dark:text-emerald-400";
  }
  return "text-slate-600 dark:text-slate-500";
}

function labelColor(isComplete: boolean, isActive: boolean): string {
  if (isComplete) {
    return "text-emerald-600 dark:text-emerald-400";
  }
  if (isActive) {
    return "text-slate-900 dark:text-white";
  }
  return "text-slate-600 dark:text-slate-500";
}

interface WizardStepperProps {
  steps: WizardStepDescriptor[];
  currentStepId: string;
  completedStepIds: ReadonlySet<string>;
  ariaLabel: string;
}

/** Generic segmented progress stepper — the visual/accessibility contract of
 * the onboarding stepper, decoupled from onboarding's step enum so any wizard
 * can drive it. One column per step: bar segment with glyph + label stacked
 * beneath, so they can never drift out of alignment. */
export const WizardStepper: FC<WizardStepperProps> = ({
  steps,
  currentStepId,
  completedStepIds,
  ariaLabel,
}) => {
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStepId),
  );

  return (
    <div
      className="w-full space-y-3"
      role="progressbar"
      aria-valuenow={currentIndex + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label={ariaLabel}
    >
      <div className="flex gap-1">
        {steps.map((step, index) => {
          const isComplete = completedStepIds.has(step.id);
          const isActive = step.id === currentStepId;

          return (
            <div
              key={step.id}
              className="flex min-w-0 flex-1 flex-col gap-2"
              aria-current={isActive ? "step" : undefined}
            >
              <div
                className={`h-1 w-full transition-all duration-300 motion-reduce:transition-none ${barColor(isComplete, isActive)}`}
              />
              <div className="flex items-center justify-center gap-1">
                <span
                  className={`font-mono text-[8px] ${glyphColor(isComplete, isActive)}`}
                >
                  {glyphFor(isComplete, isActive)}
                </span>
                <span
                  className={`font-mono text-[10px] ${glyphColor(isComplete, isActive)}`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              <span
                className={`truncate text-center font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${labelColor(isComplete, isActive)}`}
                title={step.label}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Visually hidden live region for screen readers */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Step {currentIndex + 1} of {steps.length}:{" "}
        {steps[currentIndex]?.label ?? ""}
      </div>
    </div>
  );
};
